import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { EntityMeta, Item } from '@qtek/shared/models';
import { ItemActions } from './item.actions';

export interface ItemsState {
  allItems: Record<string, Item[]>;
  itemsMeta: EntityMeta;
}

const initialState: ItemsState = {
  allItems: {},
  itemsMeta: new EntityMeta(),
};

const itemReducer = createReducer(
  initialState,
  on(ItemActions.getItemsSuccess, (state, { res, meta, mysid }) => ({
    ...state,
    allItems: {
      ...state.allItems,
      [mysid]: res,
    },
    itemsMeta: meta || { ...state.itemsMeta },
  })),
  on(ItemActions.getItemSuccess, (state, { res, mysid }) => {
    const existingItems = state.allItems[mysid] || [];
    const existingIndex = existingItems.findIndex(item => item._id === res._id);

    if (existingIndex >= 0) {
      // Update existing item
      const updatedItems = [...existingItems];
      updatedItems[existingIndex] = res;
      return {
        ...state,
        allItems: {
          ...state.allItems,
          [mysid]: updatedItems,
        },
      };
    } else {
      // Add new item
      return {
        ...state,
        allItems: {
          ...state.allItems,
          [mysid]: [...existingItems, res],
        },
      };
    }
  }),
  on(ItemActions.createItemSuccess, (state, { res, mysid }) => ({
    ...state,
    allItems: {
      ...state.allItems,
      [mysid]: [...(state.allItems[mysid] || []), res],
    },
  })),
  on(ItemActions.updateItemSuccess, (state, { res, mysid }) => {
    const newArr = state.allItems[mysid].slice();
    const index = newArr.findIndex((item: Item) => item._id === res._id);
    newArr[index] = { ...newArr[index], ...res };

    return {
      ...state,
      allItems: {
        ...state.allItems,
        [mysid]: [].concat(newArr),
      },
    };
  }),
  on(ItemActions.deleteItemSuccess, (state, { res, mysid }) => ({
    ...state,
    allItems: {
      ...state.allItems,
      [mysid]: (state.allItems[mysid] || []).filter(
        item => item._id !== res._id
      ),
    },
  })),
  on(ItemActions.unsubscribeItem, (state, { mysid }) => {
    if (!mysid) return state;

    const { [mysid]: _, ...allItems } = state.allItems;

    return {
      ...state,
      allItems,
    };
  })
);

export const ItemFeature = createFeature({
  name: 'itemKey',
  reducer: itemReducer,
  extraSelectors: ({ selectItemKeyState }) => ({
    selectItemsByMysid: (mysid: string) =>
      createSelector(
        selectItemKeyState,
        (state: ItemsState) => state?.allItems?.[mysid] || []
      ),
  }),
});

export const { selectAllItems, selectItemsMeta, selectItemsByMysid } =
  ItemFeature;
